import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Calculator, Building2, Battery, Sun, ArrowRight, CheckCircle, MapPin, Upload, Mail, Zap, Image as ImageIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';

const MAIL_RECIPIENT = 'info@en-power.eu';
const INTEREST_RATE = 0.025;
const TERM_YEARS = 20;
const ANNUITY_FACTOR = ((1 + INTEREST_RATE) ** TERM_YEARS * INTEREST_RATE) / (((1 + INTEREST_RATE) ** TERM_YEARS) - 1);
const INPUT_MIN_SIZE = 1;

const projectTypes = [
  {
    id: 'schwarmspeicher',
    name: 'Kleinspeicher / Schwarmspeicher',
    icon: Zap,
    minSize: 5,
    maxSize: 20,
    step: 1,
    unit: 'm²',
    description: 'Gewerbe, Industrie, Mischgebiet und teilweise Wohngebiet',
    oneTimeBase: { size: 5, min: 15000, max: 25000 },
    annualBase: { size: 5, max: 1600 },
    notes: ['Einmalig oder jährlich möglich', 'optional skalierbar bis 20m²', '0€ eigene Investition'],
  },
  {
    id: 'bess',
    name: 'Batteriespeicher (BESS)',
    icon: Battery,
    minSize: 80,
    maxSize: 5000,
    step: 1,
    unit: 'm²',
    description: 'Gewerbe, Industrie, Mischgebiet und geeignete Freiflächen',
    yearlyBase: { size: 1000, min: 15000, max: 30000 },
    notes: ['bis zu 30 Jahre Laufzeit', 'Nähe zum Umspannwerk vorteilhaft', '0€ eigene Investition'],
  },
  {
    id: 'freiflaeche',
    name: 'Freiflächen-PV',
    icon: Sun,
    minSize: 10000,
    maxSize: 500000,
    step: 1,
    unit: 'm²',
    description: 'Acker, Grünland, Brache, Gewerbe- und Industrieflächen',
    yearlyBase: { size: 10000, min: 2000, max: 5000 },
    notes: ['bis zu 5.000€/ha p.a.', 'bis zu 40 Jahre Laufzeit', '0€ eigene Investition'],
  },
  {
    id: 'dach',
    name: 'Dachverpachtung',
    icon: Building2,
    minSize: 400,
    maxSize: 10000,
    step: 1,
    unit: 'm²',
    description: 'Gewerbe- und Industriedächer',
    roof: true,
    notes: ['Dachsanierung statt Pacht möglich', 'bis zu 30 Jahre Laufzeit', '0€ eigene Investition'],
  },
];

const orientations = [
  { value: 'ost-west', label: 'Ost/West', oneTimePerKwp: { min: 150, max: 200 } },
  { value: 'sued', label: 'Südausrichtung', oneTimePerKwp: { min: 160, max: 200 } },
];

const formatCurrency = (value) => (
  value.toLocaleString('de-DE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  })
);

const formatNumber = (value) => value.toLocaleString('de-DE', { maximumFractionDigits: 1 });
const formatRange = (range) => `bis zu ${formatCurrency(range.max)}`;
const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

export const Pachtrechner = () => {
  const [selectedType, setSelectedType] = useState('dach');
  const [size, setSize] = useState(1000);
  const [orientation, setOrientation] = useState('ost-west');
  const [showResult, setShowResult] = useState(false);
  const [imagePreview, setImagePreview] = useState('');
  const [leadSubmitted, setLeadSubmitted] = useState(false);
  const [mapRequested, setMapRequested] = useState(false);
  const [lead, setLead] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    address: '',
    message: '',
    imageFile: null,
    privacy: false,
  });

  const currentType = projectTypes.find((type) => type.id === selectedType);
  const currentOrientation = orientations.find((item) => item.value === orientation) || orientations[0];
  const isBelowMinimum = size < currentType.minSize;
  const minimumSizeLabel = `${formatNumber(currentType.minSize)} ${currentType.unit}`;
  const enteredSizeLabel = `${formatNumber(size)} ${currentType.unit}`;

  useEffect(() => {
    return () => {
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  const result = useMemo(() => {
    if (currentType.roof) {
      const estimatedKwp = size / 5;
      const oneTimeRange = {
        min: estimatedKwp * currentOrientation.oneTimePerKwp.min,
        max: estimatedKwp * currentOrientation.oneTimePerKwp.max,
      };
      const annualRange = {
        min: oneTimeRange.min * ANNUITY_FACTOR,
        max: oneTimeRange.max * ANNUITY_FACTOR,
      };

      return {
        oneTimeRange,
        annualRange,
        monthlyRange: { min: annualRange.min / 12, max: annualRange.max / 12 },
        totalRange: { min: annualRange.min * TERM_YEARS, max: annualRange.max * TERM_YEARS },
        basis: `ca. ${formatNumber(estimatedKwp)} kWp aus ${formatNumber(size)} ${currentType.unit}`,
      };
    }

    if (currentType.oneTimeBase) {
      const oneTimeRange = {
        min: (size / currentType.oneTimeBase.size) * currentType.oneTimeBase.min,
        max: (size / currentType.oneTimeBase.size) * currentType.oneTimeBase.max,
      };
      const annualRange = currentType.annualBase
        ? {
          min: currentType.annualBase.min ? (size / currentType.annualBase.size) * currentType.annualBase.min : 0,
          max: (size / currentType.annualBase.size) * currentType.annualBase.max,
        }
        : {
          min: oneTimeRange.min * ANNUITY_FACTOR,
          max: oneTimeRange.max * ANNUITY_FACTOR,
        };

      return {
        oneTimeRange,
        annualRange,
        annualMaxOnly: currentType.annualBase && !currentType.annualBase.min,
        monthlyRange: { min: annualRange.min / 12, max: annualRange.max / 12 },
        totalRange: { min: annualRange.min * TERM_YEARS, max: annualRange.max * TERM_YEARS },
        basis: `${formatNumber(size)} ${currentType.unit}`,
      };
    }

    const annualRange = {
      min: (size / currentType.yearlyBase.size) * currentType.yearlyBase.min,
      max: (size / currentType.yearlyBase.size) * currentType.yearlyBase.max,
    };
    const oneTimeRange = {
      min: annualRange.min / ANNUITY_FACTOR,
      max: annualRange.max / ANNUITY_FACTOR,
    };

    return {
      oneTimeRange,
      annualRange,
      monthlyRange: { min: annualRange.min / 12, max: annualRange.max / 12 },
      totalRange: { min: annualRange.min * TERM_YEARS, max: annualRange.max * TERM_YEARS },
      basis: `${formatNumber(size)} ${currentType.unit}`,
    };
  }, [currentType, currentOrientation, size]);

  const mapSrc = mapRequested && lead.address.trim()
    ? `https://www.google.com/maps?q=${encodeURIComponent(lead.address.trim())}&output=embed`
    : '';

  const handleTypeChange = (typeId) => {
    const nextType = projectTypes.find((type) => type.id === typeId);
    setSelectedType(typeId);
    setSize(nextType.minSize);
    setShowResult(false);
  };

  const handleSizeChange = (nextValue) => {
    const normalized = Number(String(nextValue).replace(',', '.'));
    if (!Number.isNaN(normalized)) {
      setSize(clamp(normalized, INPUT_MIN_SIZE, currentType.maxSize));
      setShowResult(false);
    }
  };

  const handleLeadChange = (field, value) => {
    if (field === 'address') {
      setMapRequested(false);
    }
    setLead((prev) => ({ ...prev, [field]: value }));
  };

  const handleImageChange = (event) => {
    const file = event.target.files?.[0] || null;
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
    }
    setImagePreview(file ? URL.createObjectURL(file) : '');
    handleLeadChange('imageFile', file);
  };

  const handleCalculate = () => {
    setShowResult(true);
    setLeadSubmitted(false);
  };

  const handleLeadSubmit = (event) => {
    event.preventDefault();

    const body = [
      'Neuer Lead aus dem EN:POWER Pachtrechner',
      '',
      `Service: ${currentType.name}`,
      `Flächengröße: ${formatNumber(size)} ${currentType.unit}`,
      currentType.roof ? `Ausrichtung: ${currentOrientation.label}` : null,
      `Berechnungsbasis: ${result.basis}`,
      `Einmalzahlung: ${formatRange(result.oneTimeRange)}`,
      `Jährliche Zahlung: ${formatRange(result.annualRange, { maxOnly: result.annualMaxOnly })}`,
      '',
      `Adresse: ${lead.address}`,
      `Bilddatei: ${lead.imageFile?.name || 'nicht angegeben'} (bitte im E-Mail-Programm anhängen)`,
      '',
      'Kontaktdaten:',
      `Name: ${lead.name}`,
      `E-Mail: ${lead.email}`,
      `Telefon: ${lead.phone}`,
      `Unternehmen: ${lead.company || '-'}`,
      '',
      'Nachricht:',
      lead.message || '-',
    ].filter(Boolean).join('\n');

    window.location.href = `mailto:${MAIL_RECIPIENT}?subject=${encodeURIComponent(`Pachtrechner Lead - ${currentType.name}`)}&body=${encodeURIComponent(body)}`;
    setLeadSubmitted(true);
  };

  const isLeadReady = lead.name && lead.email && lead.phone && lead.address && lead.imageFile && lead.privacy;

  return (
    <div className="min-h-screen bg-slate-50 py-12 md:py-24" data-testid="pachtrechner-page">
      <div className="mx-auto mb-8 max-w-7xl px-4 sm:px-6 md:mb-12 lg:px-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#E07000] mb-4">
            Pachtrechner
          </span>
          <h1 className="font-heading mb-4 text-3xl font-bold text-[#122A5C] md:text-4xl lg:text-5xl">
            Berechnen Sie Ihr Pachtpotenzial
          </h1>
          <p className="mx-auto max-w-2xl text-base text-slate-600 md:text-lg">
            Wählen Sie den passenden Service, geben Sie Fläche und Standort ein und erhalten Sie eine erste Spanne für Einmalzahlung und jährliche Zahlung.
          </p>
        </motion.div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-5 gap-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="lg:col-span-3 space-y-8"
          >
            <Card className="overflow-hidden rounded-lg border-0 shadow-xl md:rounded-3xl">
              <CardHeader className="bg-[#122A5C] text-white p-6">
                <CardTitle className="flex items-center gap-3 font-heading text-xl">
                  <Calculator className="w-6 h-6" />
                  Ihre Flächendaten
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-7 p-5 md:space-y-8 md:p-8">
                <div>
                  <Label className="text-sm font-semibold text-[#122A5C] mb-4 block">
                    Art des Projekts
                  </Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {projectTypes.map((type) => (
                      <button
                        key={type.id}
                        type="button"
                        onClick={() => handleTypeChange(type.id)}
                        data-testid={`project-type-${type.id}`}
                          className={`rounded-lg border-2 p-3 text-left transition-all md:p-4 ${
                          selectedType === type.id
                            ? 'border-[#E07000] bg-[#E07000]/5'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <type.icon className={`w-6 h-6 mb-2 ${
                          selectedType === type.id ? 'text-[#E07000]' : 'text-slate-400'
                        }`} />
                        <div className="font-semibold text-[#122A5C] text-sm">{type.name}</div>
                        <div className="text-xs text-slate-500">{type.description}</div>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-3 mb-4">
                    <Label className="text-sm font-semibold text-[#122A5C]">
                      Flächengröße
                    </Label>
                    <div className="flex items-center gap-3">
                      <Input
                        type="number"
                        min={INPUT_MIN_SIZE}
                        max={currentType.maxSize}
                        step={currentType.step}
                        value={size}
                        onChange={(event) => handleSizeChange(event.target.value)}
                        className="w-32 rounded-xl text-right font-semibold text-[#122A5C]"
                        data-testid="size-input"
                      />
                      <span className="text-2xl font-bold text-[#E07000]">{currentType.unit}</span>
                    </div>
                  </div>
                  <Slider
                    value={[size]}
                    onValueChange={(value) => handleSizeChange(value[0])}
                    min={INPUT_MIN_SIZE}
                    max={currentType.maxSize}
                    step={currentType.step}
                    className="py-4"
                    data-testid="size-slider"
                  />
                  <div className="flex justify-between text-xs text-slate-500 mt-2">
                    <span>ab {formatNumber(INPUT_MIN_SIZE)} {currentType.unit}</span>
                    <span>{formatNumber(currentType.maxSize)} {currentType.unit}</span>
                  </div>
                  <div className="mt-3 rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-600">
                    {`Mindestfläche für ${currentType.name}: `}
                    <span className="font-semibold text-[#122A5C]">{minimumSizeLabel}</span>
                  </div>
                </div>

                {currentType.roof && (
                  <div>
                    <Label className="text-sm font-semibold text-[#122A5C] mb-3 block">
                      Ausrichtung
                    </Label>
                    <Select 
                      value={orientation} 
                      onValueChange={(value) => {
                        setOrientation(value);
                        setShowResult(false);
                      }}
                    >
                      <SelectTrigger className="rounded-xl" data-testid="orientation-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {orientations.map((item) => (
                          <SelectItem key={item.value} value={item.value}>
                            {item.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <Button 
                  onClick={handleCalculate}
                    className="w-full rounded-full bg-[#E07000] py-5 text-base font-semibold text-white hover:bg-[#C56200] md:py-6 md:text-lg btn-animate"
                  data-testid="calculate-button"
                >
                  Pacht berechnen
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>

              </CardContent>
            </Card>

            {showResult && !isBelowMinimum && (
              <Card className="overflow-hidden rounded-lg border-0 shadow-xl md:rounded-3xl" data-testid="lead-card">
                <CardHeader className="bg-white p-6 border-b border-slate-200">
                  <CardTitle className="flex items-center gap-3 font-heading text-xl text-[#122A5C]">
                    <Mail className="w-6 h-6 text-[#E07000]" />
                    Lead mit Adresse und Bild senden
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-5 md:p-8">
                  <form onSubmit={handleLeadSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="lead-name" className="text-sm font-semibold text-[#122A5C] mb-2 block">
                          Name *
                        </Label>
                        <Input
                          id="lead-name"
                          required
                          value={lead.name}
                          onChange={(event) => handleLeadChange('name', event.target.value)}
                          placeholder="Max Mustermann"
                          className="rounded-xl"
                        />
                      </div>
                      <div>
                        <Label htmlFor="lead-company" className="text-sm font-semibold text-[#122A5C] mb-2 block">
                          Unternehmen
                        </Label>
                        <Input
                          id="lead-company"
                          value={lead.company}
                          onChange={(event) => handleLeadChange('company', event.target.value)}
                          placeholder="Firma GmbH"
                          className="rounded-xl"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="lead-email" className="text-sm font-semibold text-[#122A5C] mb-2 block">
                          E-Mail *
                        </Label>
                        <Input
                          id="lead-email"
                          type="email"
                          required
                          value={lead.email}
                          onChange={(event) => handleLeadChange('email', event.target.value)}
                          placeholder="max@beispiel.de"
                          className="rounded-xl"
                        />
                      </div>
                      <div>
                        <Label htmlFor="lead-phone" className="text-sm font-semibold text-[#122A5C] mb-2 block">
                          Telefon *
                        </Label>
                        <Input
                          id="lead-phone"
                          type="tel"
                          required
                          value={lead.phone}
                          onChange={(event) => handleLeadChange('phone', event.target.value)}
                          placeholder="+49 160 1234567"
                          className="rounded-xl"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="lead-address" className="text-sm font-semibold text-[#122A5C] mb-2 block">
                        Adresse der Fläche *
                      </Label>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <Input
                          id="lead-address"
                          required
                          value={lead.address}
                          onChange={(event) => handleLeadChange('address', event.target.value)}
                          placeholder="Straße, Hausnummer, PLZ, Ort"
                          className="rounded-xl pl-10"
                        />
                      </div>
                    </div>

                    <div className="rounded-2xl overflow-hidden border border-slate-200 bg-slate-100 min-h-[240px]">
                      {mapSrc ? (
                        <iframe
                          title="Karten-Vorschau"
                          src={mapSrc}
                          className="w-full h-[260px] border-0"
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                        />
                      ) : (
                        <div className="h-[260px] flex flex-col items-center justify-center text-slate-500 text-sm gap-4 px-6 text-center">
                          <MapPin className="w-8 h-8 text-[#E07000]" />
                          <span>Adresse eingeben und Karten-Vorschau aktiv laden.</span>
                          <Button
                            type="button"
                            variant="outline"
                            disabled={!lead.address.trim()}
                            onClick={() => setMapRequested(true)}
                            className="rounded-full border-[#122A5C] text-[#122A5C] hover:bg-[#122A5C] hover:text-white disabled:opacity-50"
                          >
                            Karten-Vorschau laden
                          </Button>
                        </div>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="lead-image" className="text-sm font-semibold text-[#122A5C] mb-2 block">
                        Bild der Fläche *
                      </Label>
                      <label className="block rounded-2xl border-2 border-dashed border-slate-300 bg-white hover:border-[#E07000] transition-colors cursor-pointer overflow-hidden">
                        {imagePreview ? (
                          <img src={imagePreview} alt="Vorschau der Fläche" className="w-full h-56 object-cover" />
                        ) : (
                          <div className="h-56 flex flex-col items-center justify-center text-slate-500">
                            <Upload className="w-8 h-8 mb-3 text-[#E07000]" />
                            <span className="font-medium">Bild hochladen</span>
                            <span className="text-xs mt-1">JPG, PNG oder WebP</span>
                          </div>
                        )}
                        <Input
                          id="lead-image"
                          type="file"
                          accept="image/*"
                          required
                          onChange={handleImageChange}
                          className="sr-only"
                        />
                      </label>
                    </div>

                    <div>
                      <Label htmlFor="lead-message" className="text-sm font-semibold text-[#122A5C] mb-2 block">
                        Ergänzende Hinweise
                      </Label>
                      <Textarea
                        id="lead-message"
                        value={lead.message}
                        onChange={(event) => handleLeadChange('message', event.target.value)}
                        placeholder="z.B. Dachzustand, Trafo-Nähe, Ansprechpartner vor Ort..."
                        className="rounded-xl min-h-[110px]"
                      />
                    </div>

                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="lead-privacy"
                        required
                        checked={lead.privacy}
                        onCheckedChange={(checked) => handleLeadChange('privacy', checked)}
                      />
                      <Label htmlFor="lead-privacy" className="text-sm text-slate-600 cursor-pointer">
                        Ich habe die <a href="/datenschutz" className="text-[#E07000] hover:underline">Datenschutzerklärung</a> gelesen und stimme der Verarbeitung meiner Daten zur Bearbeitung der Anfrage zu. *
                      </Label>
                    </div>

                    <Button
                      type="submit"
                      disabled={!isLeadReady}
                      className="w-full bg-[#122A5C] hover:bg-[#0E214B] text-white rounded-full py-6 text-lg font-semibold disabled:opacity-50"
                    >
                      Lead per E-Mail vorbereiten
                      <Mail className="ml-2 w-5 h-5" />
                    </Button>

                    {leadSubmitted && (
                      <div className="flex items-start gap-3 p-4 bg-green-50 rounded-xl text-sm text-green-800">
                        <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                        <span>Die E-Mail wurde vorbereitet. Bitte prüfen Sie die Nachricht und hängen Sie das ausgewählte Bild im E-Mail-Programm an.</span>
                      </div>
                    )}
                  </form>
                </CardContent>
              </Card>
            )}
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="lg:col-span-2"
          >
            <Card className={`overflow-hidden rounded-lg border-0 shadow-xl transition-all md:rounded-3xl lg:sticky lg:top-32 ${
              showResult ? 'ring-2 ring-[#E07000]' : ''
            }`}>
              <CardHeader className="bg-white p-6 border-b border-slate-200">
                <CardTitle className="font-heading text-xl text-[#122A5C]">
                  Ihr Pachtpotenzial
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                {showResult ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="text-sm text-slate-500 mb-2">Service</div>
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-12 h-12 bg-[#E07000]/10 rounded-xl flex items-center justify-center">
                        <currentType.icon className="w-6 h-6 text-[#E07000]" />
                      </div>
                      <div>
                        <div className="font-heading font-bold text-[#122A5C]">{currentType.name}</div>
                        <div className="text-sm text-slate-500">{isBelowMinimum ? enteredSizeLabel : result.basis}</div>
                      </div>
                    </div>

                    {isBelowMinimum ? (
                      <div className="rounded-2xl border border-red-200 bg-red-50 p-5">
                        <div className="font-heading text-xl font-bold text-red-700 mb-2">
                          Angebot nicht möglich
                        </div>
                        <p className="text-sm text-red-800">
                          {`Die Mindestfläche wurde nicht erreicht. Für ${currentType.name} benötigen wir mindestens ${minimumSizeLabel}; eingegeben wurden ${enteredSizeLabel}.`}
                        </p>
                      </div>
                    ) : (
                      <>
                    <div className="space-y-4 mb-6">
                      <div className="bg-[#122A5C] rounded-2xl p-5 text-white">
                        <div className="text-sm text-white/70 mb-1">Einmalzahlung</div>
                        <div className="text-2xl md:text-3xl font-bold" data-testid="one-time-result">
                          {formatRange(result.oneTimeRange)}
                        </div>
                      </div>
                      <div className="bg-[#E07000]/10 rounded-2xl p-5">
                        <div className="text-sm text-slate-600 mb-1">Jährliche Zahlung</div>
                        <div className="text-2xl md:text-3xl font-bold text-[#E07000]" data-testid="yearly-result">
                          {formatRange(result.annualRange, { maxOnly: result.annualMaxOnly })}
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-6">
                      <div className="bg-slate-50 rounded-xl p-4 text-center">
                        <div className="text-xs text-slate-500 mb-1">Monatlich</div>
                        <div className="text-sm font-semibold text-[#122A5C]">
                          {formatRange(result.monthlyRange, { maxOnly: result.annualMaxOnly })}
                        </div>
                      </div>
                      <div className="bg-slate-50 rounded-xl p-4 text-center">
                        <div className="text-xs text-slate-500 mb-1">20 Jahre</div>
                        <div className="text-sm font-semibold text-[#122A5C]">
                          {formatRange(result.totalRange, { maxOnly: result.annualMaxOnly })}
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3">
                      {currentType.notes.map((note) => (
                        <div key={note} className="flex items-center gap-2 text-sm text-slate-600">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          <span>{note}</span>
                        </div>
                      ))}
                    </div>
                      </>
                    )}
                  </motion.div>
                ) : (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <ImageIcon className="w-8 h-8 text-slate-400" />
                    </div>
                    <p className="text-slate-500">
                      Geben Sie Ihre Flächendaten ein und klicken Sie auf "Pacht berechnen".
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
};
